import React, { useState } from "react";
import Form from "@ant-design/compatible/lib/form/Form";
import "@ant-design/compatible/assets/index.css";
import { Row, Col, Input, Modal, Button } from "antd";

import { DrawerProps } from "antd/lib/drawer";
import { FormComponentProps } from "@ant-design/compatible/lib/form/Form";
import {
	MergeTagVariable,
	AddMergeTagVariableFormValues,
	MergeTagVariableType
} from "@/Contracts/Documents";
import ZMessage from "../ZMessage";

export interface Props
	extends DrawerProps,
		FormComponentProps<AddMergeTagVariableFormValues> {
	onSubmit?: (mergeTagVariable: MergeTagVariable) => void;
	mergeTagCustomVariables: MergeTagVariable[];
	mergeTagSystemVariables: MergeTagVariable[];
	updateMergeTagVariables: (
		mergeTagVariables: MergeTagVariable[]
	) => Promise<void>;
}

export interface State {
	readonly isLoading: boolean;
}

const MergeTagAddModal: React.FC<Props> = ({
	form,
	onSubmit,
	onClose,
	visible,
	mergeTagCustomVariables = [],
	mergeTagSystemVariables = [],
	updateMergeTagVariables
}) => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const onSave = ($event: React.FormEvent<HTMLFormElement>) => {
		$event.preventDefault();
		form.validateFields(
			(
				errors: unknown,
				addVariableFormValues: AddMergeTagVariableFormValues
			) => {
				if (!errors) {
					addVariable($event, addVariableFormValues);
				}
			}
		);
	};

	const onCancel = (
		$event: React.MouseEvent<any> | React.KeyboardEvent<HTMLDivElement>
	) => {
		if (onClose) {
			onClose($event as any);
		}
	};

	const addVariable = async (
		_: React.FormEvent<HTMLFormElement>,
		addVariableFormValues: AddMergeTagVariableFormValues
	) => {
		if (
			mergeTagCustomVariables.some(
				a => a.variable_name === addVariableFormValues.variable_name
			) ||
			mergeTagSystemVariables.some(
				a => a.variable_name === addVariableFormValues.variable_name
			)
		) {
			ZMessage.error("Variable already exists!");
		} else {
			setIsLoading(true);
			try {
				const mergeVariable: MergeTagVariable = {
					variable_name: addVariableFormValues.variable_name,
					value: addVariableFormValues.value,
					base_variable_name: addVariableFormValues.variable_name,
					variable_type: MergeTagVariableType.SIMPLE,
					display_name: addVariableFormValues.variable_name
				};
				await updateMergeTagVariables([
					...mergeTagCustomVariables,
					mergeVariable
				]);
				onSubmit && onSubmit(mergeVariable);
			} catch (error) {
				console.error(error);
			} finally {
				setIsLoading(false);
			}
		}
	};
	return (
		<Modal
			visible={visible}
			title="Add new Variable"
			footer={null}
			onCancel={onCancel}
		>
			<Form
				labelCol={{ span: 24 }}
				wrapperCol={{ span: 24 }}
				onSubmit={onSave}
				className="add-variable-form"
			>
				<Row className="add-variable-form-container">
					<Col span={24}>
						<Form.Item
							label="Variable name"
							hasFeedback={true}
							help="Your input can have alphabets, numerics and special characters (only ‘dot’ and ‘underscore’)"
						>
							{form.getFieldDecorator("variable_name", {
								rules: [
									{
										required: true
									},
									{
										pattern: /^[a-zA-Z0-9\._]+?$/
									}
								]
							})(<Input placeholder="Enter variable Name" />)}
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item label="Variable value" hasFeedback={true}>
							{form.getFieldDecorator("value", {
								rules: [
									{
										required: true,
										message: "Enter variable value",
										whitespace: true
									}
								]
							})(<Input placeholder="Enter variable value" />)}
						</Form.Item>
					</Col>
				</Row>
				<div className="modal-footer">
					<Button
						key="submit"
						type="primary"
						htmlType="submit"
						disabled={isLoading}
						loading={isLoading}
						data-testid="submit-add-variable"
					>
						Save
					</Button>
					<Button
						key="cancel"
						type="link"
						onClick={onCancel}
						disabled={isLoading}
					>
						Cancel
					</Button>
				</div>
			</Form>
		</Modal>
	);
};

export default Form.create<Props>({ name: "add_variable_form" })(
	MergeTagAddModal
);
